
import { namespace } from 'vuex-class';
import { Component, Ref, Vue } from 'vue-property-decorator';

import {
	ArticlesDataFragment,
	AuthorDataFragment,
	GetAuthorByUrlDocument,
	GetAuthorByUrlQuery,
} from '@/pages/graphql/queries/GetAuthorByUrl.query';

import { ArticleType } from '@/@types/graphql-types';
import { toastError } from '@/helpers/toast/toast-helper';
import { AssertByTypename } from '@/helpers/graphql-model-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faFacebook, faInstagram, faLinkedin, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faLink } from '@fortawesome/free-solid-svg-icons';
library.add(faTwitter, faFacebook, faInstagram, faLinkedin, faLink);

import NavigationTab from '@/components/nav/NavigationTab.vue';
import InfiniteScroll from '@/components/list/InfiniteScroll.vue';
import ArticleCard from '@/components/editorial/ArticleCard.vue';

import { clickContentFilter, clickOnAuthorSocials } from '@/pages/editorial/tracking';

const Language = namespace('language');

export type ContentType = 'articles' | 'lists' | 'other';
export type AuthorPageArticle = ReturnType<typeof transformArticles>;

type ArticleEdges = AssertByTypename<GetAuthorByUrlQuery['urlV2']['node'], 'Author'>['articles']['edges'];

@Component({
	name: 'AuthorPage',
	components: { ArticleCard, NavigationTab, InfiniteScroll },
	apollo: {
		authorData: {
			query: GetAuthorByUrlDocument,
			variables() {
				return {
					fullPath: this.$route.path,
					country: this.country,
					language: this.language,
					first: 5,
					filter: {
						articleTypes: [ArticleType.NewsArticle],
					},
				};
			},
			update({ urlV2: { node } }: GetAuthorByUrlQuery) {
				const { content, articles, titleDetailArticles } = node as AssertByTypename<typeof node, 'Author'>;

				this.$store.dispatch('meta/setAuthorJsonLD', content);

				return { content, articles, titleDetailArticles };
			},
			skip() {
				return this.$route.name !== 'app.editorial.author';
			},
			errorPolicy: 'all',
			error(error) {
				captureMessageForSentry(
					'[GraphQL Get Author Page Data]:',
					{ error: error, where: 'Component: AuthorPage' },
					'error'
				);

				if (process.client) {
					toastError({ message: this.$tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
				}
			},
		},
	},
})
export default class AuthorPage extends Vue {
	/* CONTENT TYPE SWITCHING */
	contentTypes: ReadonlyArray<{ key: string; value: ContentType }> = [
		{ key: 'Lists', value: 'lists' },
		{ key: 'Articles', value: 'articles' },
		{ key: 'Other', value: 'other' },
	] as const;

	get activeContent(): ContentType {
		const query = this.$route.query?.content ?? '';
		return this.contentTypes.some(type => type.value === query) ? (query as ContentType) : 'lists';
	}

	onContentTypeChange(value: ContentType) {
		clickContentFilter({
			category: 'userinteraction',
			action: 'filter_toggled',
			label: 'content_types',
			property: value,
		});
	}

	trackSocialClicks(property: string) {
		clickOnAuthorSocials({
			category: 'person_social_media',
			action: 'clicked',
			label: 'AUTHOR',
			property,
		});
	}

	/* AUTHOR DATA */
	@Language.Getter country: string;
	@Language.Getter language: string;

	authorData: {
		content: AuthorDataFragment;
		articles: ArticlesDataFragment;
		titleDetailArticles: ArticlesDataFragment;
	};

	get author() {
		return this.authorData?.content ?? {};
	}

	get articleCount() {
		return this.authorData?.articles?.totalCount ?? 0;
	}

	get articles() {
		return transformArticles(this.authorData?.articles?.edges);
	}

	get news() {
		return this.articles.filter(article => article.type !== 'GenericTitleList');
	}

	get lists() {
		return this.articles.filter(article => article.type === 'GenericTitleList');
	}

	get titleDetailArticles() {
		return transformArticles(this.authorData?.titleDetailArticles?.edges);
	}

	get hasPublications() {
		return this.author.publications != null && this.author.publications.length > 0;
	}

	get hasSocials() {
		return this.author.socialHandles != null && this.author.socialHandles?.length > 0;
	}

	toHostname(url: string) {
		try {
			return new URL(url).hostname;
		} catch {
			return null;
		}
	}

	socialLinkIcons = ['twitter', 'facebook', 'instagram', 'linkedin'];

	/* INFINITE SCROLLING */
	@Ref('infinite-scroll') infiniteScroll: InfiniteScroll;

	get isLoadingMore() {
		return this.$apollo.queries.authorData.loading;
	}

	get isAtEndOfList() {
		return (
			!this.authorData?.articles?.pageInfo.hasNextPage ??
			!this.authorData?.titleDetailArticles?.pageInfo.hasNextPage ??
			true
		);
	}

	onLoadNext({ target }: { target?: { complete: (len: number) => void } }) {
		if (!target) return;

		this.$apollo.queries.authorData
			.fetchMore({
				variables: {
					after: this.authorData.articles.pageInfo.endCursor,
				},
				updateQuery(
					previousResult: GetAuthorByUrlQuery,
					{ fetchMoreResult }: { fetchMoreResult?: GetAuthorByUrlQuery }
				) {
					if (!fetchMoreResult) return previousResult;
					type Author = AssertByTypename<typeof previousResult.urlV2.node, 'Author'>;

					const currentAuthor = previousResult.urlV2.node as Author;
					const fetchedAthor = fetchMoreResult.urlV2.node as Author;

					// Update News Articles
					if (currentAuthor.articles.edges != null && fetchedAthor.articles.edges != null) {
						currentAuthor.articles.edges.push(...fetchedAthor.articles.edges);
						currentAuthor.articles.pageInfo = fetchedAthor.articles.pageInfo;
					}

					// Update Title Detail Articles
					if (
						currentAuthor.titleDetailArticles.edges != null &&
						fetchedAthor.titleDetailArticles.edges != null
					) {
						currentAuthor.titleDetailArticles.edges.push(...fetchedAthor.titleDetailArticles.edges);
						currentAuthor.titleDetailArticles.pageInfo = fetchedAthor.titleDetailArticles.pageInfo;
					}

					return previousResult;
				},
			})
			.catch(e => {
				captureMessageForSentry(
					'[GraphQL List Article Page Load More Titles Issue]:',
					{ error: e, where: 'Component: AuthorPage, method: onLoadNext' },
					'error'
				);
			})
			.finally(() => target.complete(this.articles.length));
	}
}

function transformArticles(edges: ArticleEdges) {
	if (!edges) return [];

	return edges
		.filter(edge => edge.node.mainObjects.length > 0)
		.map(({ node: { content, mainObjects } }) => {
			type Node = (typeof mainObjects)[number]['node'];
			type MainObject = AssertByTypename<Node, 'Movie' | 'Show' | 'Season' | 'GenericTitleList'>;

			const mainObject = mainObjects[0]?.node as MainObject;
			if (mainObject.__typename === 'GenericTitleList') {
				const [firstTitle] = mainObject.titles.edges ?? [];
				return {
					type: mainObject.__typename,
					mainObject: firstTitle?.node,
					...content,
				};
			}

			return {
				type: mainObject.__typename,
				mainObject,
				...content,
			};
		});
}
